import React from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { FilterChip } from "./FilterChip";
import { useThemeColor } from "@/hooks/useThemeColor";
import {
  IAnchorFilterCriteria,
  AnchorModel,
  Contact,
  ContactModel,
  GroupModel,
  MemoryModel,
  PlaceModel,
} from "common247";

export interface IAnchorFilterComponentProps {
  criteria: IAnchorFilterCriteria;
  onChange: (updated: IAnchorFilterCriteria) => void;
  allContacts?: (Contact | ContactModel)[];
  allGroups?: GroupModel[];
  allMemories?: MemoryModel[];
  allPlaces?: PlaceModel[];
}

const ANCHOR_TYPES: { type: "animals" | "hobbies" | "courses" | "other"; label: string; icon: string }[] = [
  { type: "animals", label: "Animals", icon: "paw-outline" },
  { type: "hobbies", label: "Hobbies", icon: "game-controller-outline" },
  { type: "courses", label: "Courses", icon: "school-outline" },
  { type: "other", label: "Other", icon: "ellipsis-horizontal-outline" },
];

/**
 * Filter controls specific to Anchor entities.
 */
export function AnchorFilter({
  criteria,
  onChange,
  allContacts = [],
  allGroups = [],
  allMemories = [],
  allPlaces = [],
}: IAnchorFilterComponentProps): React.JSX.Element {
  const textMuted = useThemeColor({}, "textMuted");

  const currentTypes = criteria.types || [];
  const currentContactIds = criteria.contactIds || [];
  const currentGroupIds = criteria.groupIds || [];
  const currentMemoryIds = criteria.memoryIds || [];
  const currentPlaceIds = criteria.placeIds || [];

  const handleTypeToggle = (type: "animals" | "hobbies" | "courses" | "other"): void => {
    const updated = currentTypes.includes(type)
      ? currentTypes.filter((t) => t !== type)
      : [...currentTypes, type];
    onChange({ ...criteria, types: updated.length > 0 ? updated : undefined });
  };

  const handleContactToggle = (contactId: string): void => {
    const updated = currentContactIds.includes(contactId)
      ? currentContactIds.filter((id) => id !== contactId)
      : [...currentContactIds, contactId];
    onChange({ ...criteria, contactIds: updated.length > 0 ? updated : undefined });
  };

  const handleGroupToggle = (groupId: string): void => {
    const updated = currentGroupIds.includes(groupId)
      ? currentGroupIds.filter((id) => id !== groupId)
      : [...currentGroupIds, groupId];
    onChange({ ...criteria, groupIds: updated.length > 0 ? updated : undefined });
  };

  const handleMemoryToggle = (memoryId: string): void => {
    const updated = currentMemoryIds.includes(memoryId)
      ? currentMemoryIds.filter((id) => id !== memoryId)
      : [...currentMemoryIds, memoryId];
    onChange({ ...criteria, memoryIds: updated.length > 0 ? updated : undefined });
  };

  const handlePlaceToggle = (placeId: string): void => {
    const updated = currentPlaceIds.includes(placeId)
      ? currentPlaceIds.filter((id) => id !== placeId)
      : [...currentPlaceIds, placeId];
    onChange({ ...criteria, placeIds: updated.length > 0 ? updated : undefined });
  };

  return (
    <View style={styles.container}>
      {/* Anchor Types */}
      <View style={styles.section}>
        <ThemedText style={[styles.label, { color: textMuted }]}>Anchor Type</ThemedText>
        <View style={styles.chipsWrap}>
          {ANCHOR_TYPES.map((item) => (
            <FilterChip
              key={item.type}
              label={item.label}
              icon={item.icon as any}
              selected={currentTypes.includes(item.type)}
              onPress={() => handleTypeToggle(item.type)}
              compact
            />
          ))}
        </View>
      </View>

      {/* Has Location Toggle */}
      <View style={styles.section}>
        <ThemedText style={[styles.label, { color: textMuted }]}>Location</ThemedText>
        <View style={styles.chipsWrap}>
          <FilterChip
            label="All Anchors"
            selected={criteria.hasLocation === undefined}
            onPress={() => onChange({ ...criteria, hasLocation: undefined })}
            compact
          />
          <FilterChip
            label="With Map Location"
            icon="location-outline"
            selected={criteria.hasLocation === true}
            onPress={() =>
              onChange({
                ...criteria,
                hasLocation: criteria.hasLocation === true ? undefined : true,
              })
            }
            compact
          />
        </View>
      </View>

      {/* Linked Memories */}
      {allMemories.length > 0 && (
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: textMuted }]}>Linked Memories</ThemedText>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.horizScroll}>
            {allMemories.map((m) => (
              <FilterChip
                key={m.id}
                label={m.title}
                icon="book-outline"
                selected={currentMemoryIds.includes(m.id)}
                onPress={() => handleMemoryToggle(m.id)}
                compact
              />
            ))}
          </ScrollView>
        </View>
      )}

      {/* Linked Contacts */}
      {allContacts.length > 0 && (
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: textMuted }]}>Linked Contacts</ThemedText>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.horizScroll}>
            {allContacts.map((c) => (
              <FilterChip
                key={c.id}
                label={c.name}
                icon="person-outline"
                selected={currentContactIds.includes(c.id)}
                onPress={() => handleContactToggle(c.id)}
                compact
              />
            ))}
          </ScrollView>
        </View>
      )}

      {/* Linked Groups */}
      {allGroups.length > 0 && (
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: textMuted }]}>Linked Groups</ThemedText>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.horizScroll}>
            {allGroups.map((g) => (
              <FilterChip
                key={g.id}
                label={g.name}
                icon="people-outline"
                selected={currentGroupIds.includes(g.id)}
                onPress={() => handleGroupToggle(g.id)}
                compact
              />
            ))}
          </ScrollView>
        </View>
      )}

      {/* Linked Places */}
      {allPlaces.length > 0 && (
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: textMuted }]}>Linked Places</ThemedText>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.horizScroll}>
            {allPlaces.map((p) => (
              <FilterChip
                key={p.id}
                label={p.name}
                icon="location-outline"
                selected={currentPlaceIds.includes(p.id)}
                onPress={() => handlePlaceToggle(p.id)}
                compact
              />
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 12,
    paddingVertical: 4,
  },
  section: {
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  chipsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  horizScroll: {
    flexGrow: 0,
  },
});
